import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { TipInput, bookTip } from './TipInput';
import { useCardPayment } from '../lib/cardTerminal';

const METHODS = [
  { key: 'card', de: 'Karte (Terminal)', en: 'Card (terminal)' },
  { key: 'cash', de: 'Bar', en: 'Cash' },
  { key: 'css_coins', de: 'CSS Coins', en: 'CSS Coins' },
  { key: 'voucher', de: 'Gutschein', en: 'Voucher' },
];

// One payment for a Mixin participant: signup fee (pay at club) + open drinks tab.
export const MixinSettleDialog = ({ target, API, L, language, onDone, onClose }) => {
  const payByCard = useCardPayment();
  const [method, setMethod] = useState('card');
  const [tip, setTip] = useState('');
  const [busy, setBusy] = useState(false);
  const [amounts, setAmounts] = useState(null);
  const [vouchers, setVouchers] = useState([]);
  const [voucherId, setVoucherId] = useState('');
  const [voucherCode, setVoucherCode] = useState('');
  useEffect(() => { setMethod(target?.method || 'card'); setTip(''); setVoucherId(''); setVoucherCode(''); }, [target]);
  useEffect(() => {
    if (!target || method !== 'voucher') return;
    API.get(`/vouchers/user/${target.p.user_id}`)
      .then(r => setVouchers((r.data || []).filter(v => (v.remaining_balance ?? v.amount ?? 0) > 0)))
      .catch(() => setVouchers([]));
  }, [target, method, API]);
  useEffect(() => {
    if (!target) return;
    setAmounts(null);
    API.get(`/admin/community/events/${target.ev.id}/participants/${target.p.user_id}/settle-with-tab`, { params: { method } })
      .then(r => setAmounts(r.data))
      .catch(e => toast.error(e.response?.data?.detail || L('Error', 'Fehler')));
  }, [target, method, API]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null;
  const { ev, p } = target;
  const fee = Number(amounts?.fee || 0);
  const tab = Number(amounts?.tab || 0);
  const total = Number(amounts?.total || 0);
  const tipNum = method === 'card' ? Math.max(0, parseFloat(tip) || 0) : 0;

  const confirm = async () => {
    setBusy(true);
    try {
      if (method === 'card') {
        const term = await payByCard(total + tipNum, `Mixin + Konto ${p.user_name}`);
        if (!term.ok) {
          toast.error(`${L('Card payment not completed', 'Kartenzahlung nicht abgeschlossen')}: ${term.reason}`, { duration: 10000 });
          return;
        }
      }
      let data;
      try {
        ({ data } = await API.post(`/admin/community/events/${ev.id}/participants/${p.user_id}/settle-with-tab`, {
          payment_method: method,
          ...(method === 'voucher' ? (voucherId ? { voucher_id: voucherId } : { voucher_code: voucherCode.trim() }) : {}),
        }));
      } catch (e) {
        const msg = e.response?.data?.detail || L('Error', 'Fehler');
        toast.error(method === 'card'
          ? `${L('Card was charged, but booking failed — please book manually in the Kasse', 'Karte wurde belastet, Verbuchung fehlgeschlagen – bitte manuell in der Kasse verbuchen')} (${(total + tipNum).toFixed(2)} CHF): ${msg}`
          : msg, { duration: method === 'card' ? 30000 : 8000 });
        return;
      }
      try { await bookTip(API, tipNum, `Mixin ${p.user_name}`); }
      catch { toast.error(L(`Tip ${tipNum.toFixed(2)} CHF could not be booked — please add it in the Kasse`, `Trinkgeld ${tipNum.toFixed(2)} CHF konnte nicht verbucht werden – bitte in der Kasse nachtragen`), { duration: 20000 }); }
      if (Math.abs(Number(data.total) - total) > 0.01) {
        toast.warning(L(`Booked ${Number(data.total).toFixed(2)} CHF differs from charged ${total.toFixed(2)} CHF`, `Verbucht ${Number(data.total).toFixed(2)} CHF weicht vom kassierten Betrag ${total.toFixed(2)} CHF ab`), { duration: 20000 });
      }
      toast.success(`${L('Paid', 'Bezahlt')} ${Number(data.total).toFixed(2)} CHF${tipNum > 0 ? ` + ${tipNum.toFixed(2)} ${L('tip', 'Trinkgeld')}` : ''} — ${L('booked to Kasse', 'in der Kasse verbucht')}`);
      if (method === 'voucher' && Number(amounts?.tab_not_covered || 0) > 0) {
        toast.info(L('Drinks are still open on the tab — collect them via the tab badge.', 'Getränke sind noch offen — über das Konto-Badge kassieren.'), { duration: 10000 });
      }
      onDone();
    } finally { setBusy(false); }
  };

  const Row = ({ label, value, bold }) => (
    <div className={`flex justify-between ${bold ? 'font-bold text-slate-900 text-base border-t border-slate-200 pt-2' : 'text-sm text-slate-600'}`}>
      <span>{label}</span><span>{value.toFixed(2)} CHF</span>
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto" data-testid="mixin-settle-dialog">
        <DialogHeader>
          <DialogTitle>{tab > 0 ? L('Collect Mixin + tab', 'Mixin + Konto kassieren') : L('Collect Mixin', 'Mixin kassieren')}</DialogTitle>
          <DialogDescription>{p.user_name} · {ev.title}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid="mixin-settle-summary">
          {fee > 0 && <Row label={L('Mixin fee', 'Mixin-Gebühr')} value={fee} />}
          {tab > 0 && <Row label={L('Drinks / tab', 'Getränke / Konto')} value={tab} />}
          {tipNum > 0 && <Row label={L('Tip', 'Trinkgeld')} value={tipNum} />}
          <Row label={L('Total', 'Total')} value={total + tipNum} bold />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {METHODS.map(m => (
            <button key={m.key} type="button" onClick={() => setMethod(m.key)} data-testid={`mixin-settle-method-${m.key}`}
              className={`px-2 py-2.5 rounded-lg border text-xs font-bold transition-colors ${method === m.key ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-400'}`}>
              {language === 'de' ? m.de : m.en}
            </button>
          ))}
        </div>
        {method === 'voucher' && (
          <div className="space-y-2" data-testid="mixin-settle-voucher">
            {vouchers.length > 0 && (
              <select value={voucherId} onChange={(e) => { setVoucherId(e.target.value); setVoucherCode(''); }}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="mixin-settle-voucher-select">
                <option value="">{L("Player's vouchers…", 'Gutscheine des Spielers…')}</option>
                {vouchers.map(v => <option key={v.id} value={v.id}>{v.code} · {Number(v.remaining_balance ?? v.amount ?? 0).toFixed(2)} CHF</option>)}
              </select>
            )}
            {!voucherId && (
              <input value={voucherCode} onChange={(e) => setVoucherCode(e.target.value)} placeholder={L('…or enter voucher code', '…oder Gutschein-Code eingeben')}
                className="w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm uppercase" data-testid="mixin-settle-voucher-code" />
            )}
            {Number(amounts?.tab_not_covered || 0) > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="mixin-settle-voucher-tab-note">
                {L(`Voucher covers the Mixin fee only — drinks (${Number(amounts.tab_not_covered).toFixed(2)} CHF) stay open on the tab.`,
                   `Gutschein deckt nur die Mixin-Gebühr — Getränke (${Number(amounts.tab_not_covered).toFixed(2)} CHF) bleiben auf dem Konto offen.`)}
              </p>
            )}
          </div>
        )}
        {method === 'card' && <TipInput value={tip} onChange={setTip} base={total} />}
        <div className="flex gap-3 pt-1">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={busy}>{L('Cancel', 'Abbrechen')}</Button>
          <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-bold" onClick={confirm} disabled={busy || !amounts || total <= 0 || (method === 'voucher' && !voucherId && !voucherCode.trim())} data-testid="mixin-settle-confirm">
            {busy ? '…' : `${L('Collect', 'Kassieren')} ${(total + tipNum).toFixed(2)}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MixinSettleDialog;
